import React, { FC, useEffect } from 'react';
import { reduxForm, Field } from 'redux-form';
import { Toggle } from '_fields/inputs';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { Form } from '@components/shadcn/components/ui/form';
import { RootState } from '@store/reducers';
import { Helmet } from 'react-helmet';
import { Text } from 'shared-components';
import withOnSubmit from '@components/Apply/withOnSubmit/withOnSubmit';
import { ApplyWizardSteps, STEP_WHEN_FUNDING_NEEDED } from '@components/ApplyWizard/ApplyWizardSteps';
import { useForm } from 'react-hook-form';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';
import { useHistory } from 'react-router-dom';
import { LoanWizardShell, LoanSubmitButton, getLoanPhaseIndex } from '@components/Apply/WizardShell';

type TypeOfFundingProps = {
  handleSubmit: () => void;
  invalid: boolean;
  whenFundingNeededDefaultValue?: string;
  saving?: boolean;
};

const WhenFundingNeededWrapper2: FC<TypeOfFundingProps> = ({
  handleSubmit,
  invalid,
  whenFundingNeededDefaultValue,
  saving: savingOnSubmit,
}) => {
  const form = useForm();
  const { saving, currentPath } = useApplyWizardContext();
  const history = useHistory();
  const phase = getLoanPhaseIndex(currentPath);
  const isSubmitting = !!(saving || savingOnSubmit);

  // The "When do you need funding?" step was removed from the loan flow
  // per Apply QA. We keep the route + component mounted so deep-links
  // and the brand-config-driven `finalWizardRoutes` don't 404, but
  // redirect to the summary on mount so the user never sees this page.
  // Drop the redirect (and this component) once brand routes are
  // updated server-side.
  useEffect(() => {
    history.replace('/app/funding-is-almost-here');
  }, [history]);

  return (
    <LoanWizardShell currentStepIndex={phase}>
      <Helmet>
        <title>Aproximate Timing</title>
      </Helmet>

      <div className="flex items-center justify-center py-[20px] w-full">
        <Text variant="headline-sm" className="!font-extended !uppercase !text-[16px] !leading-none text-center">
          When Do You Need Funding?
        </Text>
      </div>

      <div className="flex flex-col gap-[10px] items-center w-full">
        <div className="flex items-center justify-center py-[10px] w-full">
          <Text variant="body-general" className="!text-[20px] !leading-[30px] text-center">
            Approximate Timing
          </Text>
        </div>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="w-full">
            <div className="w-full flex flex-col gap-[10px]">
              <div className="w-full">
                <Field
                  component={Toggle}
                  className="w1024:grid-cols-4"
                  name="when_funding_needed"
                  defaultValue={whenFundingNeededDefaultValue}
                  buttons={[
                    { text: `Immediately`, value: 'immediately' },
                    { text: `Within 10 days`, value: 'within-10-days' },
                    { text: `Within 1 month`, value: 'within-1-month' },
                    { text: `30+ days`, value: 'within-more-than-30-days' },
                  ]}
                />
              </div>

              <div className="flex flex-col items-center justify-center w-full pt-[20px] pb-[10px] px-[10px] gap-[10px]">
                <LoanSubmitButton invalid={invalid} saving={isSubmitting} data-testid="info_continue">
                  Continue
                </LoanSubmitButton>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </LoanWizardShell>
  );
};

const WhenFundingNeededWrapper1 = reduxForm<TypeOfFundingProps, TypeOfFundingProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_WHEN_FUNDING_NEEDED].zodSchema),
})(WhenFundingNeededWrapper2);
const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    whenFundingNeededDefaultValue: appSelector(state, 'when_funding_needed'),
  };
};

export const WhenFundingNeeded = withOnSubmit(
  connect(mapStateToProps)(WhenFundingNeededWrapper1),
  STEP_WHEN_FUNDING_NEEDED
);
